/**
 * useNotifications Hook
 * React Query hook for notification state management
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DOMAIN_EVENT_CATALOG, type DomainEventType } from '@resida/shared';
import {
  getNotifications,
  getNotificationCount,
  acknowledgeNotification,
  acknowledgeAllNotifications,
} from '../api/notifications';
import { useDomainEvents, type DomainEventHandlerMap } from './useDomainEvents';

const NOTIFICATION_QUERY_KEY = ['notifications'];
const NOTIFICATION_COUNT_KEY = ['notifications', 'count'];

export function useNotifications(options?: {
  acknowledged?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: [...NOTIFICATION_QUERY_KEY, options],
    queryFn: () => getNotifications(options),
  });
}

// BR-15 — no more 30s poll (refetchInterval). The bell now invalidates reactively, on any
// bridged domain event, via useDomainEvents (see NotificationBell.tsx).
export function useNotificationCount() {
  return useQuery({
    queryKey: NOTIFICATION_COUNT_KEY,
    queryFn: getNotificationCount,
  });
}

export function useAcknowledgeNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acknowledgeNotification,
    onSuccess: () => {
      // Invalidate both queries to refresh data
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_KEY });
    },
  });
}

export function useAcknowledgeAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acknowledgeAllNotifications,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_KEY });
    },
  });
}

/**
 * BR-15 — the bell has no poll left; it invalidates reactively instead, on ANY event the
 * change-awareness bridge handles (BR-7 — every DomainEvent arm, present and future). Built
 * from `DOMAIN_EVENT_CATALOG`'s own keys so a new arm needs no edit here either.
 */
export function useNotificationsRealtimeSync(): void {
  const queryClient = useQueryClient();

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_KEY });
  }, [queryClient]);

  const handlers = useMemo<DomainEventHandlerMap>(() => {
    const map: DomainEventHandlerMap = {};
    for (const type of Object.keys(DOMAIN_EVENT_CATALOG) as DomainEventType[]) {
      map[type] = invalidate;
    }
    return map;
  }, [invalidate]);

  useDomainEvents(handlers);
}
